import { toApiDate } from "@marekl/adminer/datetime";
import { FULL_DATA_FIELDS, leaderSchema, memberSchema } from "../TeamDetailPage/memberFormSchema";
import { displayValue } from "./draftFields";
import type { DraftFieldPath, DraftPerson, DraftRow, DraftState, FieldConflict, RowErrors } from "./draftTypes";

/**
 * A trailing blank row is the spreadsheet's "next line", not data. It must not
 * count as dirty (or the guard would fire the moment the page loads), must not
 * block the submit, and must not be sent.
 */
export function isBlankNewRow(row: DraftRow): boolean {
  if (row.origin.kind !== "new") return false;
  const p = row.person;
  return (
    !p.first_name.trim() &&
    !p.last_name.trim() &&
    !p.birth_number.trim() &&
    !p.street.trim() &&
    !p.city.trim() &&
    !p.postal_code.trim() &&
    !p.phone.trim() &&
    p.birth_date == null
  );
}

export function personChanged(row: DraftRow): boolean {
  const b = row.baseline;
  if (!b) return true;
  return (
    b.first_name !== row.person.first_name ||
    b.last_name !== row.person.last_name ||
    (b.country_code || "CZ") !== row.person.country_code ||
    (b.birth_date ?? "") !== (toApiDate(row.person.birth_date) ?? "") ||
    (b.birth_number ?? "") !== row.person.birth_number ||
    (b.street ?? "") !== row.person.street ||
    (b.city ?? "") !== row.person.city ||
    (b.postal_code ?? "") !== row.person.postal_code ||
    (b.phone ?? "") !== row.person.phone
  );
}

function rolesChanged(row: DraftRow): boolean {
  const a = [...row.roles].sort();
  const b = [...row.baselineRoles].sort();
  return a.length !== b.length || a.some((r, i) => r !== b[i]);
}

export function isRowDirty(row: DraftRow): boolean {
  if (isBlankNewRow(row)) return false;
  if (row.origin.kind !== "persisted") return true;
  return row.removed || rolesChanged(row) || personChanged(row);
}

export function isDraftDirty(state: DraftState): boolean {
  return state.rows.some(isRowDirty);
}

/**
 * A row blocks the submit only when the submit will actually write it.
 *
 * Blocking on every incomplete row would trap a leader whose legacy členové
 * predate the rodné-číslo rule — they could neither fix the record nor submit
 * anything else. Untouched rows are simply not sent, so the server never sees
 * them either.
 *
 * `joining` inverts that for a club with no season registration, because the
 * reason above does not apply to it: it has no legacy členové, it is
 * declaring for the FIRST time this season who its členové are, and
 * registering carries the whole soupiska across. „Everyone except the ones
 * you did not get round to" is not an answer the federation can use, so every
 * row on the grid must be complete before the club can join. The server
 * enforces the same sweep (membership.unnamedGuestRows).
 */
export function isBlocking(row: DraftRow, joining = false): boolean {
  if (row.removed || isBlankNewRow(row)) return false;
  // The leader is exempt from the joining sweep, exactly as the server is
  // (membership.unnamedGuestRows skips them): ErrLeaderIdentityLocked puts a
  // leader's identity beyond their own reach, so blocking the club's
  // registration on the leader's gaps leaves nobody able to clear it. Their
  // row still blocks once somebody EDITS it, like any other.
  //
  // One gap is NOT covered by that exemption: an empty rodné číslo refuses
  // the whole registration server-side (ErrLeaderBirthNumberMissing), because
  // the admin who installs a leader can fill it in. It is deliberately not a
  // blocking ROW here — the leader still may not edit that cell, so a row
  // error would point at work they cannot do. SeasonRegistrationPage says it
  // in an Alert above the grid instead.
  if (joining && row.isLeader) return isRowDirty(row);
  return joining || isRowDirty(row);
}

export function clientRowErrors(row: DraftRow): RowErrors {
  const out: RowErrors = {};
  const schema = row.isLeader ? leaderSchema : memberSchema;
  const parsed = schema.safeParse({
    person: row.person,
    roles: row.roles,
    membership_kind: "registered",
  });
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const path = issue.path.join(".") as DraftFieldPath;
      if (!(path in out)) out[path] = issue.message;
    }
  }
  // Mirrors validateNoBlanking: a field that is filled in server-side may not
  // be cleared. The schema cannot express it — it has no baseline to compare.
  if (row.baseline) {
    for (const f of FULL_DATA_FIELDS) {
      if (row.baseline[f] && !row.person[f].trim()) {
        const path = `person.${f}` as DraftFieldPath;
        if (!(path in out)) out[path] = "Povinné pole";
      }
    }
  }
  return out;
}

/**
 * What a merge with the server roster left for the reader to decide
 * (draftRebase). These block like any error: neither side of a conflict is
 * preselected, and an orphaned or duplicated person needs an answer before
 * anything about them can be sent.
 */
export function mergeRowErrors(row: DraftRow): RowErrors {
  const out: RowErrors = {};
  if (row.orphaned) out.row = "Osoba byla mezitím odebrána ze soupisky. Zaregistrujte ji znovu, nebo úpravy zahoďte.";
  else if (row.duplicateOf) out.row = "Na soupisce mezitím přibyla osoba se stejným jménem a datem narození.";
  if (row.removed) return out; // a removal sends no fields to decide
  for (const [f, c] of Object.entries(row.conflicts) as [keyof DraftPerson, FieldConflict][]) {
    out[`person.${f}`] = `Mezitím změněno na „${displayValue(f, c.theirs)}“. Vyberte, která hodnota platí.`;
  }
  return out;
}

/** Rows that will be written, each with the errors that stop them. */
export function errorsByRow(state: DraftState, joining = false): Map<string, RowErrors> {
  const map = new Map<string, RowErrors>();
  for (const row of state.rows) {
    const server = row.serverErrors;
    // Server errors always show — they know things the client cannot, like a
    // duplicate rodné číslo — even on a row the client would not block.
    const client = isBlocking(row, joining) ? clientRowErrors(row) : {};
    const merged = { ...client, ...server, ...mergeRowErrors(row) };
    if (Object.keys(merged).length > 0) map.set(row.key, merged);
  }
  return map;
}

/** An existing row that is incomplete but untouched: warn, never block. */
export function isIncompleteButUntouched(row: DraftRow): boolean {
  if (isRowDirty(row) || !row.baseline) return false;
  return FULL_DATA_FIELDS.some((f) => !(row.baseline?.[f] ?? "").trim());
}

/**
 * canSubmit is true when there is something to write and nothing to fix.
 * Deliberately not "no errors anywhere" — see isBlocking.
 */
export function canSubmit(state: DraftState, joining = false): boolean {
  // A club joining the season may press with nothing staged: the act is
  // joining, not editing, and the leader is what it writes at minimum.
  const hasWork = joining || isDraftDirty(state);
  return hasWork && errorsByRow(state, joining).size === 0;
}
